import { useEffect, useRef, useState } from "react";
import type { Material, Mesh } from "three";
import type { CameraView } from "./camera";
import type { OrientationModelConfig } from "./config";
import { quaternionEulerDegrees, quaternionToXyzw, type OrientationSelection } from "./orientation";

type SceneState = {
  root: { quaternion: { set(x: number, y: number, z: number, w: number): void } };
  applyCameraView(cameraView: CameraView): void;
  render(): void;
  dispose(): void;
};

export function OrientationPanel(props: {
  cameraView: CameraView;
  config: OrientationModelConfig;
  onCameraViewChange(cameraView: CameraView): void;
  selection: OrientationSelection | null;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const scene = useRef<SceneState | null>(null);
  const latest = useRef(props.selection);
  const latestCameraView = useRef(props.cameraView);
  const onCameraViewChange = useRef(props.onCameraViewChange);
  const [renderError, setRenderError] = useState(false);
  latest.current = props.selection;
  latestCameraView.current = props.cameraView;
  onCameraViewChange.current = props.onCameraViewChange;

  useEffect(() => {
    let disposed = false;
    if (!canvas.current) return;
    async function initialize() {
      try {
        const THREE = await import("three");
        const { GLTFLoader } = await import("three/examples/jsm/loaders/GLTFLoader.js");
        const { OrbitControls } = await import("three/examples/jsm/controls/OrbitControls.js");
        if (disposed || !canvas.current) return;
        const renderer = new THREE.WebGLRenderer({ canvas: canvas.current, alpha: true, antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        const sceneWidth = 320;
        const sceneHeight = 220;
        renderer.setSize(sceneWidth, sceneHeight, false);
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        const world = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(34, sceneWidth / sceneHeight, 0.01, 20);
        const controls = new OrbitControls(camera, canvas.current);
        controls.enableDamping = false;
        controls.enablePan = false;
        controls.enableRotate = true;
        controls.enableZoom = true;
        controls.minDistance = 1.4;
        controls.maxDistance = 3.2;
        controls.minPolarAngle = 0.12;
        controls.maxPolarAngle = Math.PI - 0.12;
        let applyingCameraView = false;
        let interacting = false;
        const readCameraView = (): CameraView => ({
          position: [camera.position.x, camera.position.y, camera.position.z],
          target: [controls.target.x, controls.target.y, controls.target.z],
        });
        const applyCameraView = (cameraView: CameraView) => {
          applyingCameraView = true;
          camera.position.set(...cameraView.position);
          controls.target.set(...cameraView.target);
          controls.update();
          applyingCameraView = false;
          renderer.render(world, camera);
        };
        controls.addEventListener("start", () => { interacting = true; });
        controls.addEventListener("end", () => { interacting = false; });
        controls.addEventListener("change", () => {
          renderer.render(world, camera);
          if (interacting && !applyingCameraView) onCameraViewChange.current(readCameraView());
        });
        applyCameraView(latestCameraView.current);
        const root = new THREE.Group();
        const alignment = new THREE.Group();
        alignment.rotation.x = THREE.MathUtils.degToRad(props.config.meshAlignmentXDegrees);
        root.add(alignment);
        world.add(root);
        const gltf = await new GLTFLoader().loadAsync(props.config.assetUrl);
        if (disposed) return;
        const model = gltf.scene;
        model.traverse((item) => {
          const mesh = item as Mesh;
          if (!mesh.isMesh || !mesh.geometry) return;
          const original = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
          original.forEach((material) => material.dispose());
          mesh.material = new THREE.MeshBasicMaterial({
            color: 0x28b9aa,
            transparent: true,
            opacity: 0.68,
          });
          const edges = new THREE.LineSegments(
            new THREE.EdgesGeometry(mesh.geometry, 28),
            new THREE.LineBasicMaterial({ color: 0x9affec, transparent: true, opacity: 0.72 }),
          );
          mesh.add(edges);
        });
        const bounds = new THREE.Box3().setFromObject(model);
        const size = bounds.getSize(new THREE.Vector3());
        const scale = props.config.normalizedSize / Math.max(size.x, size.y, size.z, 1e-6);
        model.scale.setScalar(scale);
        model.position.set(-props.config.pivot[0] * scale, -props.config.pivot[1] * scale, -props.config.pivot[2] * scale);
        alignment.add(model);
        const render = () => renderer.render(world, camera);
        const dispose = () => {
          controls.dispose();
          world.traverse((item) => {
            const mesh = item as Mesh;
            mesh.geometry?.dispose?.();
            const materials: Material[] = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
            materials.forEach((material) => material.dispose());
          });
          renderer.dispose();
        };
        scene.current = { root, applyCameraView, render, dispose };
        const selection = latest.current;
        if (selection?.state === "available") {
          const [x, y, z, w] = quaternionToXyzw(selection.model);
          root.quaternion.set(x, y, z, w);
        }
        render();
      } catch {
        if (!disposed) setRenderError(true);
      }
    }
    void initialize();
    return () => {
      disposed = true;
      scene.current?.dispose();
      scene.current = null;
    };
  }, [props.config]);

  useEffect(() => {
    if (!scene.current || props.selection?.state !== "available") return;
    const [x, y, z, w] = quaternionToXyzw(props.selection.model);
    scene.current.root.quaternion.set(x, y, z, w);
    scene.current.render();
  }, [props.selection]);

  useEffect(() => {
    scene.current?.applyCameraView(props.cameraView);
  }, [props.cameraView]);

  const selection = props.selection;
  const available = selection?.state === "available";
  const euler = selection?.state === "available" ? quaternionEulerDegrees(selection.model) : null;
  return (
    <section
      className="orientation-panel"
      data-calibration={props.config.calibrationVersion}
      data-camera-position={props.cameraView.position.join(",")}
      data-camera-target={props.cameraView.target.join(",")}
    >
      <header>
        <strong>{props.config.displayName}</strong>
      </header>
      <div className="orientation-scene">
        <canvas aria-label={`${props.config.displayName} 3D model; drag to rotate view`} ref={canvas} />
        {(!available || renderError) && (
          <p className="orientation-unavailable" role="status">
            {renderError ? "3D view unavailable; signals remain available" : "Orientation unavailable at this time"}
          </p>
        )}
      </div>
      <footer>
        <span><small>Pitch</small><strong>{euler ? `${euler.pitch.toFixed(1)}°` : "—"}</strong></span>
        <span><small>Roll</small><strong>{euler ? `${euler.roll.toFixed(1)}°` : "—"}</strong></span>
        <span><small>Yaw</small><strong>{euler ? `${euler.yaw.toFixed(1)}°` : "—"}</strong></span>
      </footer>
      <p className="orientation-note">Relative orientation · yaw may drift</p>
    </section>
  );
}
